import { closest, reactive } from '@rehaje/reactive'
import {
  applyDiff,
  getIn,
  getOwnPropertyDescriptor,
  getProxyRaw,
  isArray,
  isPrivateKey,
  isProxyRawKey,
  isTerminateValue,
  keys,
  setIn,
  toArr,
  uid
} from '@rehaje/shared'

import { RR } from '../alias'
import { DefaultExposedKeys } from '../constants'
import { FatalMissingContextError } from '../errors'
import { calcArrayDiffStart } from '../lambdas/calcArrayDiffStart'
import { filterPath } from '../lambdas/filterPath'
import { DefaultDescriptor } from '../lambdas/getEnumerableOwnPropertyDescriptor'
import { is } from '../lambdas/is'
import { isEqualPath } from '../lambdas/isEqualPath'
import { isExposedNeedProxy } from '../lambdas/isExposedNeedProxy'
import { getChildrenInNArray, getElementsInNArray } from '../lambdas/nArray'
import { suspend } from '../lambdas/suspend'
import {
  toJSByIterableKeys,
  toJSONByIterableKeys
} from '../lambdas/toJSByIterableKeys'
import { runElementEffect } from '../queues'
import { LifeCycle } from './LifeCycle'
import { TypedNode } from './TypedNode'

import type {
  AnyViewModel,
  Different,
  EffectOptions,
  InternalNode,
  RehajePlugin,
  TypedSchema,
  ViewModelConstructor
} from '../types'
import type { ViewElement } from './ViewElement'
import type { ObserveOptions, Observer, toJS } from '@rehaje/reactive'
import type { Disposer, Filter, TIterator, TRecord } from '@rehaje/shared'

/**
 * 视图模型，主要面向 UI 型 DSL 的视图状态管理
 */
export abstract class ViewModel
  extends LifeCycle
  implements InternalNode<ViewModel>
{
  static __r_injection_element?: ViewElement

  __r_assigned = false

  override __r_debug_id = `viewModel:${uid()}`

  declare __r_element: ViewElement

  declare __r_exposed_keys: string[]

  __r_exposed_model = ViewModel.__r_createExposedViewModel(this)

  declare __r_from_keys: PropertyKey[]

  declare __r_from_maps: Record<PropertyKey, true>

  declare __r_proxy_raw: this | undefined
  declare getPlugin: <T extends RehajePlugin>() => T

  get __r_address() {
    return this.__r_element.address
  }

  get __r_address_source() {
    return this.__r_element.__r_address_source
  }

  get __r_children() {
    const models: ViewModel[] = []
    this.__r_node.__r_children.forEach((child) => {
      const children = getChildrenInNArray(
        child.__r_elements_graph,
        this.__r_node.__r_indexes
      )
      children.forEach((child) => {
        if (child.model) models.push(child.model)
      })
    })
    return models
  }

  get __r_descendants() {
    const models: ViewModel[] = []
    this.__r_node.__r_descendants.forEach((child) => {
      const children = getChildrenInNArray(
        child.__r_elements_graph,
        this.__r_node.__r_indexes
      )
      children.forEach((child) => {
        if (child.model) models.push(child.model)
      })
    })
    return models
  }

  get __r_entity() {
    return this.__r_element.entity?.model
  }

  get __r_index() {
    return this.__r_element.index
  }

  get __r_indexes() {
    return this.__r_element.indexes
  }

  get __r_location() {
    return this.__r_node.__r_location
  }

  get __r_node() {
    return this.__r_element.node
  }

  get __r_parent() {
    return this.__r_element.parent?.model
  }

  get __r_parents() {
    return this.__r_element.parents.map((parent) => parent.model).filter(Boolean)
  }

  get __r_root(): ViewModel {
    return this.__r_element.root.model ?? /* v8 ignore next */ this
  }

  @reactive('computed')
  get __r_schema(): TypedSchema {
    const node = this.getTypedNode()
    const defStore = this.__r_store.definition
    return defStore.__r_injectNode(() => defStore.infer(this), node)
  }

  get __r_scope() {
    return this.__r_element.scope
  }

  get __r_store() {
    return this.__r_element.store
  }

  get __r_typed_definition() {
    return this.__r_node.__r_typed_definition
  }

  /**
   * TypedNode ID(只读)
   */
  get id(): string {
    return this.__r_node.id
  }

  /**
   * TypedNode type(只读)
   */
  get type(): string {
    return this.__r_node.type
  }

  set type(type: string) {
    if (type === this.type) return
    this.__r_node.type = type
  }

  /**
   * 当前版本
   */
  get version(): string {
    return this.__r_node.version
  }

  constructor(node: TypedNode) {
    super()
    if (!ViewModel.__r_injection_element) {
      throw new FatalMissingContextError(this.type, 'ViewElement', 'transpile')
    }
    this.__r_element = ViewModel.__r_injection_element
    this.__r_element.model = this
    this.__r_fromNode(node)
    this.__r_startAutoTransform()
  }

  static __r_createExposedViewModel(model: ViewModel): ViewModel {
    function createProxy(target: any) {
      if (target instanceof ViewModel) {
        const exposedKeys =
          target.__r_exposed_keys?.slice() ?? DefaultExposedKeys?.slice()
        const ownKeys = () => exposedKeys.slice()
        if (target.getIterableKeys === ownKeys) return target
        return new Proxy(target, {
          get: (target: TRecord & ViewModel, key) => {
            if (isProxyRawKey(key)) return getProxyRaw(target)
            if (key === 'getIterableKeys') return ownKeys
            const value = target[key]
            if (key === 'toJS' || key === 'toJSON') return value
            if (typeof value === 'function')
              return value.bind(getProxyRaw(target))
            if (isPrivateKey(key)) return value
            if (isExposedNeedProxy(value)) return createProxy(value)
            return value
          },
          getOwnPropertyDescriptor: (target, key: string) => {
            if (!exposedKeys.includes(key)) return
            return getOwnPropertyDescriptor(target, key) ?? DefaultDescriptor
          },
          has: (target: TRecord, key: string) => {
            if (isPrivateKey(key)) return false
            return key in target
          },
          ownKeys: () => {
            return ownKeys()
          },
          set: (target, key, value) => {
            if (isPrivateKey(key)) target[key] = value
            return true
          }
        })
      }
      return new Proxy(target, {
        get: (target: TRecord, key) => {
          if (isProxyRawKey(key)) return getProxyRaw(target)
          const value = target[key]
          if (isPrivateKey(key)) return value
          if (isExposedNeedProxy(value)) return createProxy(value)
          return value
        },
        getOwnPropertyDescriptor: (target, key: string) => {
          if (isPrivateKey(key)) return
          return getOwnPropertyDescriptor(target, key)
        },
        has: (target: TRecord, key: string) => {
          if (isPrivateKey(key)) return false
          return key in target
        },
        ownKeys: (target) => {
          const ownKeys = keys(target)
          if (isArray(target)) ownKeys.push('length')
          return ownKeys.filter((key) => !isPrivateKey(key))
        },
        set: (target, key, value) => {
          if (isPrivateKey(key)) target[key] = value
          return true
        }
      })
    }
    return createProxy(model) as any
  }

  static __r_createViewModel<T extends ViewModelConstructor>(
    ViewModelClass: T,
    node: TypedNode,
    element: ViewElement
  ): InstanceType<T> {
    const prevInjectionElement = ViewModel.__r_injection_element
    try {
      ViewModel.__r_injection_element = element
      const model = new ViewModelClass(node)
      model.__r_initialized = true
      return model as InstanceType<T>
    } finally {
      ViewModel.__r_injection_element = prevInjectionElement
    }
  }

  __r_applyDiff(diffs: Different[] = []) {
    applyDiff(
      this,
      diffs
        .filter((diff) => !!this.__r_from_maps[diff.path[0]])
        .map((diff) => this.__r_deniseDiff(diff)),
      (diff) => this.__r_evaluate(getIn(this.__r_node, diff.path))
    )
  }

  /**
   * 差异去噪机制
   * TypedNode 的变化与 ViewModel 不一定幂等，如果遇到不幂等的场景，需要对 Diff 进行退化处理(扩大更新粒度)
   * @param diff
   */
  __r_deniseDiff(diff: Different): Different {
    const path = this.__r_ensurePatchablePath(diff.path)
    if (isEqualPath(path, diff.path)) return diff
    return {
      path,
      type: 'set'
    }
  }

  __r_ensurePatchablePath(path: PropertyKey[]) {
    return filterPath(this, path, (target) => {
      if (target === undefined || target === null) return true
      if (isTerminateValue(target)) return false
      return true
    })
  }

  /**
   * 会影响模型树的建模逻辑，不管是首次建模，自动转换，HMR 都会依赖
   */
  __r_evaluate(target: any) {
    return this.__r_node.__r_transpile(target)
  }

  /**
   * 属性建模，标记过 fromNode 的属性会同步到视图模型，没标记过的 TypedNode 属性会提前建模
   * @param this
   * @param node
   */
  __r_fromNode(
    this: TRecord & ViewModel,
    node: TRecord & TypedNode,
    evaluation = false
  ) {
    this.__r_assigned = false
    const fromNodeKeys = this.__r_from_keys
    const lazyMaps = node.__r_lazy_maps
    if (fromNodeKeys !== undefined) {
      for (const key of fromNodeKeys) {
        if (lazyMaps?.[key]) {
          if (!evaluation) continue
        } else {
          if (evaluation) continue
        }
        const nodeValue = node[key]
        if (nodeValue !== undefined) {
          this[key] = this.__r_evaluate(nodeValue)
        }
      }
    }
    // 防止 fromNode 二次拦截给误伤拦截
    this.__r_assigned = true
  }

  override __r_getEventParent() {
    return this.getParent() ?? this.__r_store
  }

  override __r_getEventTarget() {
    return this.__r_element
  }

  __r_startAutoTransform() {
    this.addWatch(
      (event) => {
        switch (event.type) {
          case 'set': {
            event.source.value = this.transformPropertyIn(
              event.path,
              event.value
            )
            break
          }
          case 'splice': {
            event.source.items = event.source.items.map((item, index) => {
              const path = event.path.concat(
                event.start - event.deleteCount + index
              )
              return this.transformPropertyIn(path, item)
            })
          }
        }
      },
      { async: false }
    )
  }

  /**
   * 添加分叉模型刷新器
   */
  addForkedRefresher(
    target: any,
    trackerPath: PropertyKey[],
    trackerNodes?: TypedNode | TypedNode[],
    onRefresh?: (start: number) => void,
    primaryKey?: PropertyKey
  ) {
    const nodes = toArr(trackerNodes)
    const destroyAndRefresh = (start = 0) => {
      if (start < 0) return
      for (let i = nodes.length - 1; i >= 0; i--) {
        const child = nodes[i]
        const elements = getElementsInNArray(
          child.__r_elements_graph,
          child.__r_indexes
        )?.flat(Infinity)
        if (elements) {
          for (let j = elements.length - 1; j >= start; j--) {
            const element = elements[j]
            element?.destroy()
          }
        }
      }
      onRefresh?.(start)
    }
    const trackerPathStr = trackerPath.join(',')
    this.addObserve(
      target,
      (event) => {
        if (event.initialPath.join(',') === trackerPathStr) {
          // 表单用法，针对数组进行 push/pop/splice
          if (!isArray(event.target)) return
          if (event.type === 'splice') {
            destroyAndRefresh(event.start)
          } else if (event.type === 'set') {
            destroyAndRefresh(
              calcArrayDiffStart(event.target, event.value, primaryKey)
            )
          }
        } else if (
          event.type === 'set' &&
          event.path.join(',') === trackerPathStr
        ) {
          // 明细用法，直接覆盖数组
          const target = event.target[event.key]
          if (!isArray(target) || !isArray(event.value)) return
          destroyAndRefresh(calcArrayDiffStart(target, event.value, primaryKey))
        }
      },
      { async: false }
    )
  }

  /**
   * 监听当前节点所有响应式属性变化
   * @param observer 监听器
   * @param options 监听参数
   */
  addWatch(
    observer: Observer,
    options?: EffectOptions & ObserveOptions
  ): Disposer {
    return this.addObserve(
      this,
      (event) => {
        if (this.is(closest(event.target, isViewModel))) observer(event)
      },
      options
    )
  }

  /**
   * 手动建模，可以针对 @lazy 装饰的属性进行强制建模
   * @param variables 局部变量
   * @returns
   */
  evaluate(variables?: TRecord): this {
    return runElementEffect(() => {
      const scope = this.__r_scope
      const node = this.__r_node as TRecord & TypedNode
      const fromKeys = node.__r_from_keys ?? []
      const fromMaps = this.__r_from_maps ?? {}
      scope.setLocalVariables(variables)
      suspend(
        () => {
          RR.undispatch(() => {
            this.__r_fromNode(node, true)

            // 对非 fromNode 子模型进行计算建模
            for (const key of fromKeys) {
              if (!fromMaps[key]) node.evaluate([key], variables)
            }
          })
        },
        (error) => this.raise(error)
      )
      return this
    }, TypedNode.__r_batchElementEffect)
  }

  /**
   * 按照指定路径查找视图模型
   * @param address
   */
  find<T extends ViewModel>(address: string): T | undefined
  /**
   * 基于过滤器查找视图模型
   * @param filter
   */
  find<T extends ViewModel = ViewModel>(filter: Filter<T>): T | undefined
  find(arg1: any): undefined | ViewModel {
    if (typeof arg1 === 'string') {
      return (
        this.__r_node.find(arg1)?.getViewModel() ??
        this.__r_element.find(arg1)?.model
      )
    } else if (typeof arg1 === 'function') {
      return this.__r_element.find((element) => {
        const model = element.model
        return model && arg1(model)
      })?.model
    }
  }

  /**
   * 基于过滤器查找视图模型集合
   * @param filter
   */
  findAll<T extends ViewModel = ViewModel>(filter: Filter<AnyViewModel>): T[]
  findAll<T extends ViewModel = ViewModel>(filter: any): T[] {
    const result: ViewModel[] = []
    if (typeof filter === 'function') {
      for (const element of this.__r_element.descendants) {
        const model = element.model
        if (model && filter(model)) result.push(model)
      }
    }
    return result as T[]
  }

  /**
   * 获取元素地址
   */
  getAddress() {
    return this.__r_address
  }

  getBelongs() {
    return this.__r_node.getBelongs()
  }

  /**
   * 获取子节点集合
   */
  getChildren<T extends ViewModel>(): T[] {
    return this.__r_children as T[]
  }

  /**
   * 获取子孙节点集合
   */
  getDescendants() {
    return this.__r_descendants
  }

  /**
   * 获取实体父亲 ViewModel
   */
  getEntity() {
    return this.__r_entity
  }

  getEntityBelongs() {
    return this.__r_node.getEntityBelongs()
  }

  /**
   * 基于路径查找属性
   * @param path
   */
  getInProperty(path: PropertyKey[] = []) {
    return getIn(this, path)
  }

  /**
   * 获取可迭代的所有属性名，序列化核心方法，如果想要定制序列化，可以重载该方法
   */
  getIterableKeys(): PropertyKey[] {
    // TODO: 装饰器装饰到 __r_iterable_keys 里
    return [
      ...new Set([
        'id',
        'type',
        ...(this.__r_exposed_keys ?? []),
        ...(this.__r_from_keys ?? [])
      ])
    ]
  }

  /**
   * 获取当前节点定位信息
   */
  getLocation() {
    return this.__r_location
  }

  /**
   * 获取父模型
   */
  getParent<T extends ViewModel>(): T | undefined {
    return this.__r_parent as T | undefined
  }

  /**
   * 获取父节点集合(从自身父节点开始)
   */
  getParents() {
    return this.__r_parents
  }

  /**
   * 获取根节点
   */
  getRoot<T extends ViewModel = ViewModel>(): T {
    return this.__r_root as T
  }

  /**
   * 获取状态中心
   */
  getStore() {
    return this.__r_store
  }

  /**
   * 获取类型
   */
  getType(): string {
    return this.__r_node.__r_type
  }

  /**
   * 获取类型定义
   */
  getTypedDefinition() {
    return this.__r_typed_definition
  }

  /**
   * 获取当前视图模型的 TypedNode
   */
  getTypedNode<T extends TypedNode>(): T {
    return this.__r_element.node as T
  }

  /**
   * 获取当前视图模型的 ADT Schema
   * @param path 可指定路径查找具体 Schema
   */
  getTypedSchema<T extends TypedSchema>(path: PropertyKey[] = []): T {
    if (path.length === 0) return this.__r_schema as T
    return this.__r_store.definition.getInWithSchema(this.__r_schema, path) as T
  }

  /**
   * 获取当前节点全局唯一 ID
   */
  getUniqueID() {
    return this.__r_node.__r_unique_id
  }

  /**
   * 获取视图元素节点
   */
  getViewElement() {
    return this.__r_element
  }

  /**
   * 获取当前视图模型索引
   */
  getViewIndex() {
    return this.__r_index
  }

  /**
   * 获取当前视图模型多维索引
   */
  getViewIndexes() {
    return this.__r_indexes
  }

  /**
   * 获取视图作用域
   */
  getViewScope() {
    return this.__r_scope
  }

  override hasOwnProperty(key: string): boolean {
    return this.getIterableKeys().includes(key)
  }

  /**
   * 判断目标对象是否是当前节点，因为考虑到有些场景 ViewModel 可能会被 Proxy 包装，引用会发生变化
   * 所以需要一个特殊的方法来判断目标节点是否是当前节点
   * @param target 目标对象
   */
  is(target?: any): target is this {
    return is(this, target)
  }

  /**
   * 是否是根节点
   */
  isRoot() {
    return this.__r_parent === undefined
  }

  /**
   * 渲染当前节点
   * @returns
   */
  render() {
    return this.__r_node.render()
  }

  /**
   * 按照指定路径给当前模型赋值
   * @param path
   * @param value
   */
  setInProperty(path: PropertyKey[] = [], value?: any) {
    setIn(this, path, value)
  }

  /**
   * 设置监听器，监听当前节点所有响应式属性变化，重复调用会自动销毁上一个同 key watch
   * @param key 唯一键
   * @param observer 监听器
   * @param options 监听参数
   */
  setWatch(
    key: string,
    observer: Observer,
    options?: EffectOptions & ObserveOptions
  ): Disposer {
    return this.setObserve(
      key,
      this,
      (event) => {
        if (closest(event.target, isViewModel) === this) observer(event)
      },
      options
    )
  }

  [Symbol.iterator](this: this & TRecord): TIterator<TRecord> {
    const entries = this.getIterableKeys().map((key) => [key, this[key]])
    return entries[Symbol.iterator]()
  }

  testPropertyIn(path: PropertyKey[] = [], value: any): boolean {
    return this.__r_node.testPropertyIn(path, value)
  }

  /**
   * 序列化视图模型
   * @param visitor 递归转换器，可定制序列化规则
   */
  toJS(visitor?: typeof toJS.visitor): TRecord {
    return toJSByIterableKeys(this, visitor)
  }

  /**
   * 序列化视图模型
   */
  toJSON(visitor?: typeof toJS.visitor): TRecord {
    return toJSONByIterableKeys(this, visitor)
  }

  /**
   * 基于指定路径的 Schema 进行类型转换
   * @param path 路径
   * @param value 转换值
   */
  transformPropertyIn(path: PropertyKey[] = [], value: any) {
    return this.__r_node.transformPropertyIn(path, value)
  }

  /**
   * 从当前节点开始往下深度递归遍历所有子孙模型
   * @param visitor 迭代器
   */
  traverse<T extends ViewModel>(visitor: (model: T) => void): void {
    visitor?.(this as any)
    for (const model of this.__r_descendants) visitor?.(model as any)
  }

  validatePropertyIn(path: PropertyKey[] = [], value: any): Error[] {
    return this.__r_node.validatePropertyIn(path, value)
  }
}

export function isViewModel(value: any): value is ViewModel {
  return value instanceof ViewModel
}
